/**
 * D-EZ-102 — STORYLINES TO WATCH.
 *
 * The TV panel before the green: who won this race last time, who is hot, who
 * is feuding, who is on the playoff bubble, which milestones are in play, and
 * what kind of afternoon the track tends to give. Everything comes from the
 * league's history as it stood BEFORE this race (`BoothSeasonContext`) and the
 * week that set the grid. Nothing is predicted; every line is a fact going in.
 */

import type { BoothSeasonContext } from '../booth/context.ts';
import { trackTemperFor } from '../houseRules.ts';
import { listOf, ordinal, plural } from './raceStory.ts';

export type StorylineKind =
  | 'defending'
  | 'champion'
  | 'points'
  | 'bubble'
  | 'alive'
  | 'hot'
  | 'feud'
  | 'milestone'
  | 'track'
  | 'weather'
  | 'temper';

export interface Storyline {
  kind: StorylineKind;
  title: string;
  text: string;
  driverIds: string[];
}

export interface StorylineExtras {
  /** Pairs feuding going into this race (from the week). */
  feuds?: readonly { a: string; b: string }[];
  /** Name lookup for the feud pairs. */
  nameOf?: (id: string) => string;
  /** The week's bad-weather line, if qualifying was rained out. */
  weather?: string | null;
  /** The track's category, for its temper. */
  trackCategory?: string | null;
  /** Is the track-personality house rule in force? Default true. */
  trackPersonality?: boolean;
  /** Is the superspeedway pack-racing house rule in force? Default true. */
  packRacing?: boolean;
  /** Only drivers in the field are worth a storyline. Default: everyone. */
  fieldIds?: readonly string[];
}

/** The house rules' own words (houseRules.ts), for the kinds of track they name. */
const TEMPER_WORDS: Record<string, { rule: 'trackPersonality' | 'packRacing'; text: string }> = {
  'Short Track': {
    rule: 'trackPersonality',
    text: 'Short track — tempers run short here, and short tracks bring out more cautions than any other kind.',
  },
  'Super Speedway': {
    rule: 'packRacing',
    text: 'Superspeedway — every car can work the draft, so the lead can swap hands all afternoon.',
  },
};

/** At most this many, so the panel reads like a TV graphic and not a report. */
export const MAX_STORYLINES = 7;

export function storylinesFor(ctx: BoothSeasonContext | null, extras: StorylineExtras = {}): Storyline[] {
  const out: Storyline[] = [];
  const inField = (id: string) => !extras.fieldIds || extras.fieldIds.includes(id);
  const nameOf = extras.nameOf ?? ((id: string) => id);

  if (ctx?.defendingRaceWinner) {
    const d = ctx.defendingRaceWinner;
    out.push({
      kind: 'defending',
      title: 'Defending winner',
      text: `${d.name} won this race in season ${d.seasonNumber}${inField(d.driverId) ? ' and is back to defend it.' : ' and is not in the field.'}`,
      driverIds: [d.driverId],
    });
  }
  if (ctx?.trackWinners.length) {
    const top = ctx.trackWinners.filter((t) => inField(t.driverId) && t.driverId !== ctx.defendingRaceWinner?.driverId);
    const best = top[0];
    if (best && best.wins >= 2) {
      out.push({
        kind: 'track',
        title: 'Knows the way round',
        text: `${best.name} has won ${plural(best.wins, 'time')} at this track.`,
        driverIds: [best.driverId],
      });
    }
  }
  if (ctx?.raceNumber === 1 && ctx.defendingChampion) {
    const c = ctx.defendingChampion;
    out.push({
      kind: 'champion',
      title: 'The champion returns',
      text: `${c.name} starts his title defence as the season ${c.seasonNumber} champion.`,
      driverIds: [c.driverId],
    });
  }
  if (ctx?.pointsLeader) {
    const p = ctx.pointsLeader;
    out.push({
      kind: 'points',
      title: 'Points leader',
      text: `${p.name} leads the standings by ${plural(p.marginToSecond, 'point')} with ${plural(p.wins, 'win')}.`,
      driverIds: [p.driverId],
    });
  }
  const playoff = ctx?.playoff;
  if (playoff?.phase === 'regular-season' && playoff.racesToCut <= 10 && playoff.cutLine) {
    const out1 = playoff.bubble.filter((b) => b.side === 'out').slice(0, 2);
    const in1 = playoff.bubble.filter((b) => b.side === 'in').slice(0, 1);
    const bits = [
      `${playoff.cutLine.name} holds the last spot on points`,
      ...in1.map((b) => `${b.name} is ${plural(b.gap, 'point')} above the line`),
      ...out1.map((b) => `${b.name} is ${plural(-b.gap, 'point')} short`),
    ];
    out.push({
      kind: 'bubble',
      title: 'On the bubble',
      text: `${plural(playoff.racesToCut, 'race')} until the playoff cut. ${bits.join('; ')}.`,
      driverIds: [playoff.cutLine.driverId, ...out1.map((b) => b.driverId), ...in1.map((b) => b.driverId)],
    });
  } else if (playoff?.phase === 'playoff' && playoff.alive.length > 0) {
    const close = playoff.bubble.slice(0, 2);
    out.push({
      kind: 'alive',
      title: 'Playoff picture',
      text: `${plural(playoff.alive.length, 'driver')} still alive for the title; ${plural(playoff.racesToCut, 'race')} left in this round${
        playoff.cutLine ? `, with ${playoff.cutLine.name} on the cut line` : ''
      }${close.length ? ` — ${listOf(close.map((b) => `${b.name} ${b.gap >= 0 ? '+' : ''}${b.gap}`))}` : ''}.`,
      driverIds: playoff.alive,
    });
  }
  const hot = (ctx?.activeStreaks ?? []).filter((s) => inField(s.driverId)).slice(0, 2);
  for (const s of hot) {
    out.push({
      kind: 'hot',
      title: s.kind === 'wins' ? 'On a roll' : 'Running well',
      text:
        s.kind === 'wins'
          ? `${s.name} has won ${s.length} in a row.`
          : s.kind === 'top5'
            ? `${s.name} has ${s.length} straight top-five finishes.`
            : s.kind === 'top10'
              ? `${s.name} has ${s.length} straight top-ten finishes.`
              : `${s.name} has been running at the finish ${s.length} races straight.`,
      driverIds: [s.driverId],
    });
  }
  // Feuds in this week's field; a pair with history across seasons says so.
  for (const f of (extras.feuds ?? []).slice(0, 2)) {
    const history = ctx?.rivalries.find((r) => (r.a === f.a && r.b === f.b) || (r.a === f.b && r.b === f.a));
    const a = nameOf(f.a);
    const b = nameOf(f.b);
    out.push({
      kind: 'feud',
      title: 'Bad blood',
      text:
        history && history.feuds >= 2
          ? `${a} and ${b} are feuding again — feud number ${history.feuds} between them${history.seasons.length > 1 ? `, across ${plural(history.seasons.length, 'season')}` : ''}.`
          : `${a} and ${b} come into this one feuding.`,
      driverIds: [f.a, f.b],
    });
  }
  const milestones = (ctx?.milestonesInPlay ?? [])
    .filter((m) => inField(m.driverId) && (m.kind === 'nth-win' || m.kind === 'nth-start' || m.kind === 'drought'))
    .slice(0, 2);
  for (const m of milestones) {
    out.push({ kind: 'milestone', title: 'Milestone watch', text: m.text, driverIds: [m.driverId] });
  }
  if (extras.weather) {
    out.push({ kind: 'weather', title: 'Weather', text: extras.weather, driverIds: [] });
  }
  const temper = extras.trackCategory ? TEMPER_WORDS[extras.trackCategory] : undefined;
  if (
    temper &&
    extras[temper.rule] !== false &&
    (temper.rule !== 'trackPersonality' || trackTemperFor(extras.trackCategory ?? '') > 0)
  ) {
    out.push({ kind: 'temper', title: 'The track', text: temper.text, driverIds: [] });
  }
  return out.slice(0, MAX_STORYLINES);
}

export { ordinal };
